import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiAccountService } from '@services/api-account.service';
import { ApiCustomerService } from '@services/api-customer.service';
import { SessionManager } from '@services/session-manager';
import { SharedService } from '@services/shared.service';
import { FormBase } from '@shared/form-base.component';
import { CustomerFindResultModel, CustomerModel } from '@shared/models/CustomerModel';
import { RegisterModel, RegisterPhoneModel } from '@shared/models/RegisterModel';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-verify-phone-form',
    templateUrl: './verify-phone-form.component.html',
    styleUrls: ['./verify-phone-form.component.scss']
})
export class VerifyPhoneFormComponent extends FormBase<RegisterModel> implements OnInit {

    user: CustomerModel;

    // Se utiliza para manejar el estado de la verificación
    isVerificationSuccess = false;
    inviteCode: string = null;
    phoneDuplicated = false;

    registerForm: UntypedFormGroup;

    // Evento para ejecutar en el componente hijo
    onReset = new Subject<boolean>()
    isVisiblePwd = false;
    codeAlert: string;
    requestAt: Date;

    constructor(private router: Router, public sharedService: SharedService, private apiAccountService: ApiAccountService, private apiCustomerService: ApiCustomerService, private formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnInit(): void {

        const session = this.apiAccountService.getSession();
        if (session && session.user) {
            this.user = session.user;
            this._formInit();
        } else {
            this.user = null
            this.apiAccountService.logout(this.router);
        }
    }

    // Form

    isInvalid(): boolean {

        // Es invalido si el email ya existe
        if (this.phoneDuplicated) return true;

        // Si los datos son validos compro var si la verificación se envío
        const invalid = this.registerForm.invalid;
        if (invalid && !this.isVerificationSuccess) {
            return true;
        } else if (this.isVerificationSuccess) {
            if (this.phone.value?.number?.length) {
                return false;
            }
            return true;
        }
    }

    isInvalidCode(): boolean {

        return this.codeConfirmation?.value?.length != 6;
    }

    get phone() { return this._formGet(this.registerForm, 'phone'); }
    get invalidPhone(): boolean {
        if (this._isInvalid(this.registerForm, 'phone', this.touched)) {
            return true;
        }
        return false;
    }

    get codeConfirmation() { return this._formGet(this.registerForm, 'codeConfirmation'); }
    get invalidCodeConfirmation(): boolean {
        return this._isInvalid(this.registerForm, 'codeConfirmation', this.touched);
    }

    private _formInit(): void {

        // Reset for hide form errors
        this.touched = true;

        let phone = { number: this.user.phone, countryCode: this.user.phoneCountry, dialCode: '+506' };

        this.registerForm = this.formBuilder.group(
            {
                phone: [
                    phone,
                    [Validators.required, Validators.minLength(8)]
                ],
                codeConfirmation: new UntypedFormControl(
                    '',
                    []
                )
            },
        );
    }


    // End Form

    onPhoneChange(ev: any) {

        // Limpiar error
        this.phoneDuplicated = false;
    }

    onCheckPhone() {

        if (this.invalidPhone) return;

        this.phoneDuplicated = null;

        const registerModel = new RegisterModel({
            phoneCountry: this.phone.value?.dialCode,
            phone: this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim(),
        });

        this.onFindCustomerByPhone(registerModel);
    }

    onFindCustomerByPhone(registerModel: RegisterModel): void {

        this.phoneDuplicated = false;

        this.apiCustomerService.findCustomerByPhone(registerModel.phoneCountry, registerModel.phone, true).then(
            (customerResult: CustomerFindResultModel) => {

                //if the call succeeds the phone was already registered
                this.phoneDuplicated = true;

            }, (err) => {

                //If the call returns a not found means that the phone wasnt registered yet
                this.phoneDuplicated = false;
            }
        );
    }

    onVerifyPhone(showAlert = false): void {

        if (this.invalidPhone) return;

        this.requestAt = new Date();

        const registerModel = new RegisterModel({
            phoneCountry: this.phone.value?.dialCode,
            phone: this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim(),
            requestAt: this.requestAt
        });

        if (this.phoneDuplicated) return;

        this.isVerificationSuccess = true;
        this.phoneDuplicated = false;

        this.sharedService.setLoading(true);

        this.apiAccountService.verifyPhone(registerModel).then(
            () => {


                this.isVerificationSuccess = true;
                this.phoneDuplicated = false;

                this.sharedService.setLoading(false);
            },
            (error: any) => {

                this.isVerificationSuccess = false;

                this.sharedService.setLoading(false); // hide loader

                if (typeof error?.error == "string") {

                    if (error?.error.toLowerCase().indexOf("vinculado") >= 0) {
                        // retorno un usuario vinculado
                        this.phoneDuplicated = true;
                        const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    } else if (error?.error.toLowerCase().indexOf("requerido") >= 0) {
                        const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    }
                } else {
                    // display error
                    const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error verificando el teléfono.');
                    this.sharedService.showErrorAlert(null, errMsg);
                }
            }
        );

        if (showAlert) {
            // Mostrar alerta
            this.codeAlert = 'Código de verificación reenviado';

            // Ocultar la alerta
            setTimeout(_ => this.codeAlert = null, 5000); // 5 segundos

        }
    }

    onSubmit() {

        // if is invalid, show the form errors
        if (this.registerForm.invalid || this.phoneDuplicated) {

            this.touched = false;
            return;
        }

        const registerPhoneDto = new RegisterPhoneModel();
        registerPhoneDto.id = this.user.id;
        registerPhoneDto.phoneCountry = this.phone.value?.dialCode;
        registerPhoneDto.phone = this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim();
        registerPhoneDto.code = this.codeConfirmation.value?.trim();
        registerPhoneDto.requestAt = this.requestAt;

        this.sharedService.setLoading(true);

        const session = this.apiAccountService.getSession();

        this.apiAccountService.registerPhone(registerPhoneDto).then(
            (result: RegisterPhoneModel) => {

                this.sharedService.setLoading(false);
                session.user.phoneVerifiedAt = result.phoneVerifiedAt ?? new Date();
                SessionManager.setSession(session);
                this.sharedService.setShowVerifyAccountModal(false);

                this.sharedService.showSuccessAlert(
                    null, `El teléfono fue verificado correctamente`
                ).then(_ => window.location.reload());
            },
            (error: any) => {

                this.sharedService.setLoading(false); // hide loader
                // display error
                const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error al actualizar la cuenta.');
                this.sharedService.showErrorAlert(null, errMsg);
            }
        );
    }

    onContact() {

        window.open('http://wa.me/50670703344?text=¡Hola!%20Tengo%20una%20pregunta:%20');
    }

}
